import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { FormApi } from 'final-form';
import { useCallback } from 'react';
import { Field, Form } from 'react-final-form';
import { ConnectedProps, connect } from 'react-redux';

import { notify } from '@/actions/ui';
import { isValidPassword } from '@/auth';
import { PasswordText } from '@/auth/components/PasswordTextField';
import { SETTINGS_ACTION_BUTTON, SETTINGS_ACTION_SLOT } from '@/auth/components/shared';
import { resetPassword, setPassword } from '@/auth/store';
import { changeCognitoPassword } from '@/auth/utils/cognitoPassword';
import { isCognitoLoginEnabled, isCognitoSessionActive } from '@/auth/utils/cognitoSession';
import { CognitoSignInError } from '@/auth/utils/cognitoSignIn';
import Button from '@/components/Button/Button';
import SettingsButton from '@/componentsv2/Button';
import { hasErrorCode } from '@/core/api';
import ERROR_CODES from '@/core/apiErrorCodes';
import { RootState } from '@/store';

const useStyles = makeStyles({
  form: {
    maxWidth: 350,
  },
  actions: {
    marginTop: 25,
  },
});

type FormData = {
  hasPassword: boolean;
  currentPassword: string;
  newPassword: string;
};

type FormErrors = Partial<Record<keyof FormData, string>>;

function validate(values: FormData): FormErrors {
  const errors: FormErrors = {};

  if (values.hasPassword && !values.currentPassword) {
    errors.currentPassword = 'Required';
  }

  if (!values.newPassword) {
    errors.newPassword = 'Required';
  } else if (!isValidPassword(values.newPassword)) {
    errors.newPassword = 'Password does not meet the requirements';
  }

  if (values.newPassword && values.newPassword === values.currentPassword) {
    errors.newPassword = 'New password must be different from the current';
  }

  return errors;
}

function isCognitoSessionInUse(): boolean {
  return isCognitoLoginEnabled() && isCognitoSessionActive();
}

// ENG-6104: Cognito answers in its own codes, mapped by changeCognitoPassword.
function cognitoErrors(e: CognitoSignInError): FormErrors | undefined {
  switch (e.code) {
    case ERROR_CODES.AUTH_INVALID_CREDENTIALS:
      return { currentPassword: 'Current password is incorrect.' };
    case ERROR_CODES.AUTH_PASSWORD_REQUIREMENTS:
      return { newPassword: 'Password does not meet the requirements' };
    case ERROR_CODES.AUTH_PASSWORD_REUSED:
      return { newPassword: 'Cannot reuse a recent password.' };
  }
}

interface ChangePasswordProps {
  token?: string;
  /**
   * ENG-6104: asked before a `token` reset; true means another flow took over
   * (cognito-auth resolved after the page rendered) and the token is left unused.
   */
  takeOverTokenReset?: () => boolean;
  userId?: string;
  onSubmit?: (values: FormData) => void;
  successNotification?: boolean;
}

const connector = connect(
  (state: RootState, ownProps: ChangePasswordProps) => {
    const userId = ownProps.userId || state.viewer.id;
    const user = state.users[userId] || {};
    return {
      userId,
      hasPassword: user.has_password,
    };
  },
  {
    setPassword,
    resetPassword,
    notify,
  }
);

const ChangePassword = ({
  token,
  takeOverTokenReset,
  hasPassword,
  userId,
  onSubmit,
  successNotification = true,
  setPassword,
  resetPassword,
  notify,
}: ChangePasswordProps & ConnectedProps<typeof connector>) => {
  const s = useStyles();
  // ENG-6104: a Cognito session changes the password in Cognito, which always
  // needs the current one. Without one (flag off, or a legacy token) the api
  // does it. Checked again at submit, since the session can start or end later.
  // ENG-6300: someone who only signs in with Google or Microsoft has no password
  // to give, so the api sets their first one (and copies it to Cognito).
  const firstPassword = !token && hasPassword === false;
  const needsCurrentPassword =
    !!hasPassword || (!token && !firstPassword && isCognitoSessionInUse());

  const handleFinalSubmit = useCallback(
    async (
      values: FormData,
      form: FormApi<FormData, FormData>
    ): Promise<FormErrors | undefined> => {
      try {
        if (token) {
          if (takeOverTokenReset?.()) return;
          await resetPassword(token, values.newPassword);
        } else if (!firstPassword && isCognitoSessionInUse()) {
          // The session may have started after render, when the field was hidden.
          if (!values.currentPassword) return { currentPassword: 'Required' };
          await changeCognitoPassword(values.currentPassword, values.newPassword);
        } else {
          await setPassword(userId, values.currentPassword, values.newPassword);
        }

        if (successNotification) notify('Password updated.');

        form.restart();

        if (onSubmit) onSubmit(values);
      } catch (e: unknown) {
        const mapped = e instanceof CognitoSignInError ? cognitoErrors(e) : undefined;
        if (mapped) return mapped;

        if (e instanceof CognitoSignInError && e.code === ERROR_CODES.AUTH_TOO_MANY_ATTEMPTS) {
          notify('Too many attempts. Please wait a few minutes and try again.', 'error');
          return;
        }

        if (hasErrorCode(e, ERROR_CODES.AUTH_PASSWORD_NOT_EQUAL)) {
          return { currentPassword: 'Current password is incorrect.' };
        }

        if (hasErrorCode(e, ERROR_CODES.AUTH_PASSWORD_REUSED)) {
          return { newPassword: 'Cannot reuse the last 5 passwords.' };
        }

        if (hasErrorCode(e, ERROR_CODES.AUTH_TOKEN_EXPIRED) || (e as any).isPermissionError) {
          notify('Reset password link has expired.', 'error');
        } else {
          notify('An error occurred when trying to set the password.', 'error');
        }
      }
    },
    [
      firstPassword,
      notify,
      onSubmit,
      resetPassword,
      setPassword,
      successNotification,
      takeOverTokenReset,
      token,
      userId,
    ]
  );

  const initialValues = { hasPassword: needsCurrentPassword, currentPassword: '', newPassword: '' };

  return (
    <Form
      onSubmit={handleFinalSubmit}
      initialValues={initialValues}
      validate={validate}
      subscription={{ submitting: true }}
    >
      {({ handleSubmit }) => {
        // Re-read on each form render, so a session that started after mount shows the field.
        const showCurrentPassword =
          !token && (hasPassword || (!firstPassword && isCognitoSessionInUse()));
        return (
          <form
            className={clsx(
              token ? s.form : 'flex flex-col gap-16 sm:flex-row sm:items-start sm:justify-between'
            )}
            onSubmit={handleSubmit}
          >
            <div className={clsx(!token && 'w-full sm:max-w-[350px]')}>
              {showCurrentPassword && (
                <Field
                  id="currentPassword"
                  changeOnBlur={false}
                  component={PasswordText}
                  name="currentPassword"
                  label="Current Password"
                  variant="outlined"
                />
              )}
              <Field
                id="newPassword"
                changeOnBlur={false}
                component={PasswordText}
                name="newPassword"
                label="New Password"
                showHelp
                variant="outlined"
                inputProps={{ autoComplete: 'new-password' }}
                current={false}
              />
            </div>
            {token ? (
              <div className={s.actions}>
                <Button type="submit" size="medium" id="updatePasswordButton">
                  Update Password
                </Button>
              </div>
            ) : (
              // ENG-6300: on the right like the page's other actions, the same size, level with the first field.
              <SettingsButton
                type="submit"
                size="medium"
                id="updatePasswordButton"
                className={clsx(SETTINGS_ACTION_SLOT, SETTINGS_ACTION_BUTTON, 'sm:mt-24')}
              >
                {firstPassword ? 'Set password' : 'Update password'}
              </SettingsButton>
            )}
          </form>
        );
      }}
    </Form>
  );
};

export default connector(ChangePassword);
